import { FunctionComponent, useEffect, useState } from "react";
import BasicCard from "@components/cards/BasicCard";
import {
  EstimateResultItem,
  EstimateResultSynthesis,
} from "@type/estimateResults/estimateResults.response";
import { ReactComponent as Arrow } from "../../static/icons/icon-arrow.svg";
import {
  RESULT_ITEMS_FARMER,
  RESULT_ITEMS_COLLECTIVITY,
  ResultSectionEnum,
} from "@utils/enums/results.enum";
import { useTranslation } from "react-i18next";
import { ReactComponent as Earth } from "../../static/images/earth.svg";
import { ReactComponent as Weight } from "../../static/images/weight.svg";
import { ReactComponent as Revenue } from "../../static/images/revenue.svg";
import { ReactComponent as Collectivity } from "../../static/images/collectivity.svg";
import { ReactComponent as Vegetal } from "../../static/images/vegetal.svg";
import { ReactComponent as Jobs } from "../../static/images/jobs.svg";
import { ReactComponent as Light } from "../../static/images/light.svg";
import { ReactComponent as Leaf } from "src/static/images/leaf.svg";
import { ReactComponent as Ozone } from "src/static/images/ozone.svg";
import HTMLReactParser from "html-react-parser";

interface ResultDetailsProps {
  synthesis: EstimateResultSynthesis;
  resultDetails: EstimateResultItem[];
  resultImpacts?: EstimateResultItem[];
  type: "farmer" | "collectivity";
  className?: string;
  onOpenFaq: (category: string) => any;
  onOpenTips: () => void;
}

const ResultDetails: FunctionComponent<ResultDetailsProps> = ({
  synthesis,
  resultDetails,
  resultImpacts,
  type,
  onOpenFaq,
  onOpenTips,
}) => {
  const { t } = useTranslation();
  const [resultSectionsBody, setResultSectionsBody] =
    useState<Map<string, EstimateResultItem[]>>();
  const [resultSectionsHeader, setResultSectionsHeader] =
    useState<Map<string, EstimateResultItem[]>>();

  const resultItems =
    type === "farmer" ? RESULT_ITEMS_FARMER : RESULT_ITEMS_COLLECTIVITY;

  const computeResultSections = (items: EstimateResultItem[]) => {
    const resultIndicatorBodyMap = new Map<string, EstimateResultItem[]>();
    const resultIndicatorHeaderMap = new Map<string, EstimateResultItem[]>();
    for (const section in ResultSectionEnum) {
      const bodyItems = items.filter(
        (item) =>
          resultItems[item.key]?.section === section &&
          !resultItems[item.key]?.header
      );
      const headerItems = items.filter(
        (item) =>
          resultItems[item.key]?.section === section &&
          resultItems[item.key]?.header
      );
      if (bodyItems.length > 0) {
        resultIndicatorBodyMap.set(section, bodyItems);
      }
      if (headerItems.length > 0) {
        resultIndicatorHeaderMap.set(section, headerItems);
      }
    }

    setResultSectionsBody(resultIndicatorBodyMap);
    setResultSectionsHeader(resultIndicatorHeaderMap);
  };

  useEffect(() => {
    if (resultDetails && resultDetails.length > 0)
      computeResultSections(resultDetails);
  }, [resultDetails]);

  const renderIcon = (name: string, className: string) => {
    switch (name) {
      case "earth":
        return (
          <Earth className={className} aria-hidden="true" focusable="false" />
        );
      case "weight":
        return (
          <Weight className={className} aria-hidden="true" focusable="false" />
        );
      case "revenue":
        return (
          <Revenue className={className} aria-hidden="true" focusable="false" />
        );
      case "collectivity":
        return (
          <Collectivity
            className={className}
            aria-hidden="true"
            focusable="false"
          />
        );
      case "vegetal":
        return (
          <Vegetal className={className} aria-hidden="true" focusable="false" />
        );
      case "jobs":
        return (
          <Jobs className={className} aria-hidden="true" focusable="false" />
        );
      case "ozone":
        return (
          <Ozone className={className} aria-hidden="true" focusable="false" />
        );
      default:
        return <></>;
    }
  };

  const renderMainResultItem = (item: EstimateResultItem) => {
    return (
      <div className="result-item-main" key={item.key}>
        {item &&
          resultItems[item.key]?.icon &&
          renderIcon(
            resultItems[item.key]?.icon ?? "",
            "result-item-icon me-3"
          )}
        <div>
          <div className="d-flex align-items-baseline flex-wrap">
            {item.value && item.value[0] && (
              <>
                {item.value[0]?.value !== "" && (
                  <>
                    <span className="stat-value me-2">
                      {item.value[0]?.value ?? "-"}
                    </span>
                    {item.value[0]?.unit && (
                      <span className="stat-unit font-weight-bold">
                        {HTMLReactParser(
                          t(
                            `result_units.${item.value[0]?.unit}`,
                            item.value[0]?.unit
                          )
                        )}
                      </span>
                    )}
                  </>
                )}
              </>
            )}
          </div>
          {item && item.label && (
            <div className={"stat-description"}>
              {HTMLReactParser(item.label)}
            </div>
          )}
          {item && item.value[0]?.description && (
            <div className="result-item-main-description text-sm">
              {HTMLReactParser(item.value[0]?.description)}
            </div>
          )}
        </div>
      </div>
    );
  };

  const renderResultItem = (item: EstimateResultItem) => {
    return (
      <div className="result-item text-black" key={item.key}>
        <div>
          {item && item.label && (
            <div className="text-black font-weight-bold text-sm mb-2">
              {HTMLReactParser(item.label)}
            </div>
          )}
          {item.value.map((val, index) => (
            <div key={item.key + index} className="mb-2">
              <div className="d-flex align-items-center flex-wrap text-primary">
                <span className="stat-value me-2">
                  {Array.isArray(val.value)
                    ? val.value.length > 2
                      ? val.value.join(", ")
                      : HTMLReactParser(
                          `<span class="font-weight-normal"></span>${val.value[0]}<span class="font-weight-normal"> à </span>${val.value[1]}`
                        )
                    : val?.value ?? "-"}
                </span>
                {val.unit ? (
                  <span className="stat-unit">
                    {HTMLReactParser(t(`result_units.${val.unit}`, val.unit))}
                  </span>
                ) : (
                  val.description && (
                    <span className="stat-description text-tertiary text-sm">
                      {HTMLReactParser(val.description)}
                    </span>
                  )
                )}
              </div>
              {val.unit && val.description && (
                <div className="text-tertiary text-sm mt-1 mb-2">
                  {HTMLReactParser(val.description)}
                </div>
              )}
            </div>
          ))}
        </div>
      </div>
    );
  };

  return (
    <>
      <div className="result-details">
        <>
          {type === "farmer" && (
            <h3 className="px-4 px-sm-0 mb-4">
              Estimation de votre potentiel financier et agronomique
            </h3>
          )}
          {resultSectionsHeader && resultSectionsHeader.has("MAIN") && (
            <BasicCard className="result-details-main d-flex flex-row mb-3">
              <>
                {resultSectionsHeader
                  .get("MAIN")
                  ?.map((item) => renderMainResultItem(item))}
              </>
            </BasicCard>
          )}

          {resultSectionsBody &&
            [...resultSectionsBody.keys()].filter(
              (section) => section !== "IMPACT"
            ).length > 0 && (
              <>
                {type === "farmer" &&
                  resultSectionsBody &&
                  [...resultSectionsBody.keys()].map((section, index) => (
                    <BasicCard
                      key={index}
                      className="result-details-section mb-3"
                    >
                      <>
                        <h4 className="result-details-section-title p-4 mb-0">
                          {section === "AGRONOMIC" ? (
                            <Leaf aria-hidden="true" focusable="false" />
                          ) : (
                            <Revenue aria-hidden="true" focusable="false" />
                          )}
                          {t(`results.details.sections.${section}`)}
                        </h4>
                        <div className="result-items d-flex flex-row align-items-stretch flex-wrap">
                          {resultSectionsBody &&
                            [...resultSectionsBody.keys()]
                              .filter(
                                (resultSection) => resultSection === section
                              )
                              .map((section) =>
                                resultSectionsBody
                                  .get(section)
                                  ?.map((item) => renderResultItem(item))
                              )}
                          {section === "AGRONOMIC" && (
                            <div className="result-item">
                              <BasicCard
                                className="card-grey card-small p-4 d-flex align-items-center justify-content-between h-100 w-100"
                                onClick={() => {
                                  window.open(
                                    t("results.details.valorisation-info.url"),
                                    "_blank"
                                  );
                                }}
                                role="link"
                              >
                                <>
                                  <a className="text-black">
                                    {t(
                                      "results.details.valorisation-info.label"
                                    )}
                                  </a>
                                  <Arrow
                                    className="ms-3 result-item-arrow"
                                    aria-hidden="true"
                                    focusable="false"
                                  />
                                </>
                              </BasicCard>
                            </div>
                          )}
                          {(section === "ECONOMIC" ||
                            section === "ENVIRONMENTAL") &&
                            synthesis?.projectProfile && (
                              <div className="result-item">
                                <BasicCard
                                  className="card-grey card-small p-4 d-flex align-items-center justify-content-between h-100 w-100"
                                  onClick={() => {
                                    window.open(
                                      t(
                                        `results.synthesis.projectProfile.${synthesis.projectProfile}.url`
                                      ),
                                      "_blank"
                                    );
                                  }}
                                >
                                  <>
                                    <a className="text-black" target="_blank">
                                      {t(
                                        `results.synthesis.projectProfile.${synthesis.projectProfile}.description`
                                      )}
                                    </a>
                                    <Arrow
                                      className="ms-3 result-item-arrow"
                                      aria-hidden="true"
                                      focusable="false"
                                    />
                                  </>
                                </BasicCard>
                              </div>
                            )}
                          {section === "IMPACT" && synthesis?.projectProfile && (
                            <div className="result-item">
                              <BasicCard
                                className="card-grey card-small p-4 d-flex align-items-center justify-content-between h-100 w-100"
                                onClick={() => {
                                  window.open(
                                    t("results.details.reassure.url"),
                                    "_blank"
                                  );
                                }}
                              >
                                <>
                                  <a className="text-black" target="_blank">
                                    {t("results.details.reassure.label")}
                                  </a>
                                  <Arrow
                                    className="ms-3 result-item-arrow"
                                    aria-hidden="true"
                                    focusable="false"
                                  />
                                </>
                              </BasicCard>
                            </div>
                          )}
                        </div>
                      </>
                    </BasicCard>
                  ))}
                {type === "collectivity" &&
                  resultSectionsBody &&
                  [...resultSectionsBody.keys()].filter(
                    (section) => section !== "IMPACT"
                  ) && (
                    <BasicCard className="result-details-section mb-3">
                      <>
                        <h4 className="result-details-section-title p-4 mb-0">
                          <Earth aria-hidden="true" focusable="false" />
                          {t(`results.details.sections.LOCAL`)}
                        </h4>
                        <div className="result-items d-flex flex-row align-items-stretch flex-wrap">
                          {resultSectionsBody &&
                            [...resultSectionsBody.keys()]
                              .filter((section) => section !== "IMPACT")
                              .map((section) =>
                                resultSectionsBody
                                  .get(section)
                                  ?.map((item) => renderResultItem(item))
                              )}
                          {synthesis.projectProfile && (
                            <div className="result-item">
                              <BasicCard
                                className="card-grey card-small p-4 d-flex align-items-center justify-content-between h-100 w-100"
                                onClick={() =>
                                  window.open(
                                    t(
                                      `results.synthesis.projectProfile.${synthesis.projectProfile}.url`
                                    ),
                                    "_blank"
                                  )
                                }
                              >
                                <>
                                  <a className="text-black">
                                    {t(
                                      `results.synthesis.projectProfile.${synthesis.projectProfile}.description`
                                    )}
                                  </a>
                                  <Arrow
                                    className="ms-3 result-item-arrow"
                                    aria-hidden="true"
                                    focusable="false"
                                  />
                                </>
                              </BasicCard>
                            </div>
                          )}
                        </div>
                      </>
                    </BasicCard>
                  )}
              </>
            )}
          <div className="result-details-link d-flex justify-content-center mt-4">
            <a
              className="text-sm text-primary font-weight-bold cursor-pointer"
              onClick={() => {
                onOpenFaq(`calcul-${type}`);
              }}
              tabIndex={0}
              onKeyPress={(e) => {
                if (e.key === "Enter" || e.key === " ") {
                  e.preventDefault();
                  onOpenFaq(`calcul-${type}`);
                }
              }}
            >
              <Light aria-hidden="true" focusable="false" />
              Comment est-ce calculé ?
            </a>
          </div>
        </>
      </div>
    </>
  );
};

export default ResultDetails;
